'use client';

import { useState } from 'react';
import { saveBillingAddressAction } from '../../../../actions/address/saveBillingAddressAction';
import type { AddressFormData, BillingType, CartAddressData } from '../../../types';
import {
  createBillingAddressFormData,
  getBillingAddressFields,
  removeBillingFieldError,
  updateBillingAddress
} from '../utils';

interface UseBillingAddressDialogSaveOptions {
  billingAddress?: CartAddressData;
  billingFormData?: Partial<AddressFormData>;
  billingType: BillingType;
  constants: Record<string, string>;
  onFieldChange: (field: string) => void;
  onSaved: (address: CartAddressData) => void;
  onClose: () => void;
}

interface BillingAddressDialogSaveController {
  billingDialogFieldErrors: Partial<Record<string, string>>;
  isBillingAddressSaving: boolean;
  handleBillingAddressSave: () => Promise<void>;
  handleBillingFieldChange: (field: string) => void;
  clearBillingDialogFieldErrors: () => void;
}

/** Handles billing address persistence and maps server validation errors to fields. */
export function useBillingAddressDialogSave({
  billingAddress,
  billingFormData,
  billingType,
  constants,
  onFieldChange,
  onSaved,
  onClose
}: UseBillingAddressDialogSaveOptions): BillingAddressDialogSaveController {
  const [billingDialogFieldErrors, setBillingDialogFieldErrors] = useState<Partial<Record<string, string>>>({});
  const [isBillingAddressSaving, setIsBillingAddressSaving] = useState(false);

  const clearBillingDialogFieldErrors = () => setBillingDialogFieldErrors({});

  const handleBillingAddressSave = async () => {
    const billingFields = getBillingAddressFields(billingAddress, billingFormData);
    const formData = createBillingAddressFormData(billingType, billingFields);

    setIsBillingAddressSaving(true);

    try {
      const result = await saveBillingAddressAction(constants, null, formData);

      if (result && !('ceweSuggestion' in result) && result.fieldErrors) {
        setBillingDialogFieldErrors(result.fieldErrors);

        return;
      }

      setBillingDialogFieldErrors({});
      onSaved(updateBillingAddress(billingAddress, billingFields));
      onClose();
    } finally {
      setIsBillingAddressSaving(false);
    }
  };

  const handleBillingFieldChange = (field: string) => {
    onFieldChange(field);
    setBillingDialogFieldErrors(errors => removeBillingFieldError(errors, field));
  };

  return {
    billingDialogFieldErrors,
    isBillingAddressSaving,
    handleBillingAddressSave,
    handleBillingFieldChange,
    clearBillingDialogFieldErrors
  };
}
